import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { RevisionLicenseConclusionScope } from '../../../../shared/model-typings/revisionlicenseconclusion';
import useGetRevisionConclusions from '../../../hooks/api/correctionsV2/useGetRevisionConclusions';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import { ConcludeMenuButton } from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/ConcludeMenuButton';
import { emptyConclusionData } from '../../../../shared/types/licenses';
import type { ChooseParams } from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/hooks/useConcludeLicense';

type Props = {
  licenseId: string;
  revisionLocator: string;
  projectLocator: string;
  /** How the project is named in the confirm dialogs, e.g. "my-app 1a2b3c4". */
  packageName: string;
  organizationId: number;
};

export const ConcludeLicenseButton: VoidFunctionComponent<Props> = ({
  licenseId,
  revisionLocator,
  projectLocator,
  packageName,
  organizationId,
}) => {
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId);
  const { data: conclusions } = useGetRevisionConclusions(revisionLocator);

  const isConcluded = (conclusions ?? emptyConclusionData).scoped.licenses.includes(licenseId);

  const revisionScope = useMemo(() => ({
    scope: RevisionLicenseConclusionScope.REVISION,
    projectLocator,
    revisionLocator,
  } as const), [revisionLocator, projectLocator]);
  const chooseParams: ChooseParams = useMemo(() => ({
    dependencyRevisionLocator: revisionLocator,
    scope: revisionScope,
    originId: revisionLocator,
  }), [revisionLocator, revisionScope]);

  if (!isLicenseConcludedEnabled) {
    return null;
  }

  return (
    // The button sits inside a clickable license row, and the menu renders inline, so keep clicks from selecting it.
    <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <ConcludeMenuButton
        licenseId={licenseId}
        isConcluded={isConcluded}
        packageName={packageName}
        conclusions={conclusions ?? emptyConclusionData}
        chooseParams={chooseParams}
        readScope={revisionScope}
        localNoun="project"
        size={BUTTON_SIZE.MICRO}
        idleType={BUTTON_TYPE.OUTLINE}
      />
    </span>
  );
};
